import { EVENT_MEDIA_OPTIONS, type EventMediaType } from '@/constants/sponsor'
import { cn } from '@/lib/utils'
import { createMarkup } from '@/constants/event'
import { DndContext } from '@dnd-kit/core'
import {
  useBannerDragDrop,
  type AllElementPositions,
} from '@/hooks/useBannerDragDrop'
import { DraggableText } from '../DraggableText'

interface BannerData {
  adName?: string
  adDescription?: string
  linkText?: string
  redirectionLink?: string
  sponsorLogoUrl?: string | null
  eventImageUrl?: string | null
  eventMediaType?: EventMediaType
  showTitle?: boolean
  showDescription?: boolean
  namePosition?: { x: number; y: number }
  descriptionPosition?: { x: number; y: number }
  sponsorLogoPosition?: { x: number; y: number }
  linkPosition?: { x: number; y: number }
}

interface InlineBannerProps {
  data: BannerData
  editable?: boolean
  onPositionChange?: (positions: AllElementPositions) => void
}

const InlineBanner = ({
  data,
  editable = false,
  onPositionChange,
}: InlineBannerProps) => {
  const {
    adName,
    adDescription,
    linkText,
    redirectionLink,
    sponsorLogoUrl,
    eventImageUrl,
    eventMediaType = EVENT_MEDIA_OPTIONS.IMAGE,
    showTitle = true,
    showDescription = true,
    namePosition: initialNamePosition,
    descriptionPosition: initialDescriptionPosition,
    sponsorLogoPosition: initialSponsorLogoPosition,
    linkPosition: initialLinkPosition,
  } = data

  const {
    containerRef,
    namePosition,
    descriptionPosition,
    sponsorLogoPosition,
    linkPosition,
    handleDragEnd,
  } = useBannerDragDrop({
    initialNamePosition,
    initialDescriptionPosition,
    initialSponsorLogoPosition,
    initialLinkPosition,
    defaultNamePosition: { x: 4, y: 8 }, // ~24px/600px = 4%, ~24px/300px = 8%
    defaultDescriptionPosition: { x: 4, y: 33.33 }, // ~24px/600px = 4%, ~100px/300px = 33.33%
    defaultSponsorLogoPosition: { x: 80, y: 76.67 }, // ~480px/600px = 80%, ~230px/300px = 76.67%
    defaultLinkPosition: { x: 4, y: 76.67 }, // ~24px/600px = 4%, ~230px/300px = 76.67%
    onPositionChange,
  })

  return (
    <section
      id="banner"
      aria-labelledby="banner-banner-heading"
      className="w-full"
    >
      <div className="flex justify-center">
        <DndContext onDragEnd={handleDragEnd}>
          <div
            ref={containerRef}
            className="relative w-full max-w-[600px] min-h-[300px] lg:h-[300px] rounded-2xl overflow-hidden shadow-lg transition-transform duration-300"
          >
            {/* Background Image/Video */}
            {eventImageUrl ? (
              eventMediaType === EVENT_MEDIA_OPTIONS.VIDEO ? (
                <video
                  src={eventImageUrl}
                  className="absolute inset-0 w-full h-full object-cover object-center select-none pointer-events-none"
                  autoPlay
                  loop
                  muted
                  playsInline
                  aria-label={
                    adName
                      ? `${adName} banner video background`
                      : 'Advertisement banner video background'
                  }
                />
              ) : (
                <img
                  src={eventImageUrl}
                  alt={
                    adName
                      ? `${adName} banner background`
                      : 'Advertisement banner background'
                  }
                  className="absolute inset-0 w-full h-full object-cover object-center transition-transform duration-300 select-none pointer-events-none"
                  draggable={false}
                />
              )
            ) : (
              <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-ss-neutral-200 to-ss-neutral-300" />
            )}

            {/* Dark Overlay for better text readability */}
            <div
              className="absolute inset-0 bg-gradient-to-r from-ss-neutral-950/90 via-ss-neutral-950/60 to-transparent"
              aria-hidden="true"
            />

            {/* Content */}
            <div className="relative z-10 h-full flex flex-col justify-between p-6 sm:p-8 lg:p-8">
              {/* Text Content */}
              <div className="flex flex-col gap-2.5 sm:gap-3 lg:gap-2.5 flex-1">
                {showTitle && adName && (
                  <DraggableText
                    position={namePosition}
                    isEditable={editable}
                    id="title"
                  >
                    <h2
                      style={{
                        fontFamily: 'ClashDisplay, sans-serif',
                      }}
                      id="banner-banner-heading"
                      className="font-[ClashDisplay] font-bold tracking-tight wrap-anywhere"
                      dangerouslySetInnerHTML={createMarkup(adName || '')}
                    ></h2>
                  </DraggableText>
                )}
                {showDescription && adDescription && (
                  <DraggableText
                    position={descriptionPosition}
                    isEditable={editable}
                    id="description"
                  >
                    <p
                      className="font-bold leading-[1.5] max-w-lg wrap-anywhere line-clamp-6"
                      dangerouslySetInnerHTML={createMarkup(adDescription)}
                    />
                  </DraggableText>
                )}
              </div>

              {/* CTA Button */}
              <div className="flex justify-between items-center mt-3">
                {linkText && (
                  <DraggableText
                    position={linkPosition}
                    isEditable={editable}
                    id="button"
                  >
                    <a
                      aria-label={
                        linkText
                          ? `${linkText} - advertisement call to action`
                          : 'Advertisement call to action'
                      }
                      className={cn(
                        'relative bg-white text-ss-neutral-950 border-white font-bold text-sm sm:text-base lg:text-base px-4 sm:px-5 lg:px-[19px] py-2 sm:py-1 lg:py-1.5 rounded-full h-auto',
                        'transition-all duration-300 ease-out group overflow-hidden',
                        'focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-ss-neutral-950',
                        'tracking-[0.64px] leading-normal shadow-sm hover:bg-white/90 '
                      )}
                      href={redirectionLink}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      {/* Button Text */}
                      <span className="relative z-10 transition-colors duration-150 ease-out w-full">
                        {linkText}
                      </span>
                    </a>
                  </DraggableText>
                )}
                {sponsorLogoUrl && (
                  <DraggableText
                    position={sponsorLogoPosition}
                    isEditable={editable}
                    id="logo"
                  >
                    <div className="flex flex-col items-end  self-end ml-auto">
                      <p className="text-white text-sm sm:text-base lg:text-base m-0 text-left">
                        Sponsored by
                      </p>
                      <img
                        src={sponsorLogoUrl}
                        alt="Sponsor logo"
                        className="max-h-8 max-w-20 object-contain"
                      />
                    </div>
                  </DraggableText>
                )}
              </div>
            </div>
          </div>
        </DndContext>
      </div>
    </section>
  )
}

export default InlineBanner
